import type { ParamValues } from '../types';
import { clamp01, mod, remap, smootherstep } from '../math/scalar';
import { axisDistance } from './helpers';
import { definePattern } from './registry';

/**
 * Utility fields.
 *
 * These carry no motif of their own; they exist to be blended with other
 * layers — a gradient multiplied over a relief fades it across the wall, a
 * checker masks alternate panels, and `flat` supplies a constant offset.
 */

interface FlatParams extends ParamValues {
  level: number;
}

export const flatPattern = definePattern<FlatParams>({
  id: 'flat',
  name: 'Flat',
  description: 'A constant height. Useful as a base layer or as a plain panel.',
  category: 'utility',
  tags: ['constant', 'mask'],
  params: [
    {
      kind: 'number',
      key: 'level',
      label: 'Level',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
    },
  ],
  sample(_sample, params) {
    return params.level;
  },
});

interface GradientParams extends ParamValues {
  mode: 'linear' | 'radial' | 'panel-x' | 'panel-y' | 'panel-radial';
  angle: number;
  start: number;
  end: number;
  ease: boolean;
}

export const gradientPattern = definePattern<GradientParams>({
  id: 'gradient',
  name: 'Gradient',
  description:
    'A smooth ramp across the wall or within each panel. Multiply it over a relief to fade the depth towards an edge or a corner.',
  category: 'utility',
  tags: ['mask', 'fade', 'modulation'],
  params: [
    {
      kind: 'select',
      key: 'mode',
      label: 'Mode',
      options: [
        { value: 'linear', label: 'Linear across the wall' },
        { value: 'radial', label: 'Radial from the wall centre' },
        { value: 'panel-x', label: 'Across each panel (X)' },
        { value: 'panel-y', label: 'Across each panel (Y)' },
        { value: 'panel-radial', label: 'Radial within each panel' },
      ],
      default: 'linear',
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Direction', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'start',
      label: 'Start',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Range',
    },
    {
      kind: 'number',
      key: 'end',
      label: 'End',
      min: 0,
      max: 1,
      step: 0.01,
      default: 1,
      group: 'Range',
    },
    { kind: 'boolean', key: 'ease', label: 'Ease', default: true, group: 'Range' },
  ],
  sample(sample, params) {
    const { wall } = sample;
    let t: number;

    switch (params.mode) {
      case 'radial': {
        const dx = sample.x - wall.width * 0.5;
        const dy = sample.y - wall.height * 0.5;
        const reach = Math.hypot(wall.width, wall.height) * 0.5;
        t = clamp01(Math.hypot(dx, dy) / Math.max(reach, 1e-3));
        break;
      }
      case 'panel-x':
        t = sample.u;
        break;
      case 'panel-y':
        t = sample.v;
        break;
      case 'panel-radial': {
        const du = (sample.u - 0.5) * 2;
        const dv = (sample.v - 0.5) * 2;
        t = clamp01(Math.hypot(du, dv) / Math.SQRT2);
        break;
      }
      case 'linear':
      default: {
        const reach =
          Math.abs(wall.width * Math.cos((params.angle * Math.PI) / 180)) +
          Math.abs(wall.height * Math.sin((params.angle * Math.PI) / 180));
        const distance = axisDistance(sample.x, sample.y, params.angle);
        // Shift so that the ramp starts at the wall corner nearest the axis.
        const origin = Math.min(0, axisDistance(wall.width, wall.height, params.angle));
        t = clamp01((distance - origin) / Math.max(reach, 1e-3));
        break;
      }
    }

    const eased = params.ease ? smootherstep(0, 1, t) : t;
    return clamp01(remap(eased, 0, 1, params.start, params.end));
  },
});

interface CheckerParams extends ParamValues {
  period: number;
  low: number;
  high: number;
  diagonal: boolean;
}

export const checkerPattern = definePattern<CheckerParams>({
  id: 'checker',
  name: 'Checker',
  description:
    'Alternates between two levels panel by panel. Blend it over a relief to give neighbouring panels different depths.',
  category: 'utility',
  tags: ['mask', 'per-panel'],
  params: [
    {
      kind: 'int',
      key: 'period',
      label: 'Period',
      min: 1,
      max: 8,
      default: 1,
      help: 'How many panels share a value before it flips.',
      group: 'Shape',
    },
    { kind: 'boolean', key: 'diagonal', label: 'Diagonal bands', default: false, group: 'Shape' },
    {
      kind: 'number',
      key: 'low',
      label: 'Low level',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.35,
      group: 'Range',
    },
    {
      kind: 'number',
      key: 'high',
      label: 'High level',
      min: 0,
      max: 1,
      step: 0.01,
      default: 1,
      group: 'Range',
    },
  ],
  sample(sample, params) {
    const column = Math.floor(sample.panel.column / params.period);
    const row = Math.floor(sample.panel.row / params.period);
    const index = params.diagonal ? column + row : mod(column + row, 2) === 0 ? 0 : 1;
    const on = params.diagonal ? mod(index, 2) === 0 : index === 0;
    return on ? params.high : params.low;
  },
});
